import { useMemo } from "react";
import type { AiEvent, AiChatSession, DerivedMessage } from "@/types/ai-chat";
import type { ContentBlock, ChatTurn } from "@/types/content-blocks";

export function deriveContentBlocks(
  events: AiEvent[],
  userMessages: DerivedMessage[],
): ChatTurn[] {
  const turns: ChatTurn[] = [];
  let currentBlocks: ContentBlock[] = [];
  let currentTextBuffer = "";
  let thinkingBuffer = "";
  let isThinkingStreaming = false;
  let isStreaming = false;
  let turnCostUsd: number | undefined;
  let turnTokens: { input: number; output: number } | undefined;
  let turnProvider = events[0]?.source_provider;

  const toolResults = new Map<string, { status: "success" | "error"; output?: string }>();
  for (const evt of events) {
    if (evt.kind === "tool_output") {
      const p = evt.payload as { tool_id: string; status: "success" | "error"; output_summary?: string };
      toolResults.set(p.tool_id, { status: p.status, output: p.output_summary });
    }
  }

  let userMsgIdx = 0;

  function flushThinking() {
    if (thinkingBuffer.length > 0) {
      currentBlocks.push({ type: "thinking", text: thinkingBuffer, isStreaming: isThinkingStreaming });
      thinkingBuffer = "";
      isThinkingStreaming = false;
    }
  }

  function flushText() {
    if (currentTextBuffer.length > 0) {
      currentBlocks.push({ type: "text", text: currentTextBuffer });
      currentTextBuffer = "";
    }
  }

  function flushAssistantTurn() {
    const wasThinkingStreaming = isThinkingStreaming;
    flushThinking();
    flushText();
    if (currentBlocks.length > 0) {
      turns.push({
        id: `turn-${turns.length}`,
        role: "assistant",
        blocks: currentBlocks,
        provider: turnProvider,
        costUsd: turnCostUsd,
        tokenCount: turnTokens,
        isStreaming: isStreaming || wasThinkingStreaming,
      });
      currentBlocks = [];
      turnCostUsd = undefined;
      turnTokens = undefined;
      isStreaming = false;
    }
  }

  function insertPendingUserMessages(beforeSeq: number) {
    while (userMsgIdx < userMessages.length) {
      const um = userMessages[userMsgIdx];
      if (um.role !== "user") { userMsgIdx++; continue; }
      if (um.eventSeqRange[0] < beforeSeq) {
        flushAssistantTurn();
        turns.push({
          id: um.id,
          role: "user",
          blocks: [{ type: "text", text: um.content }],
          isStreaming: false,
        });
        userMsgIdx++;
      } else {
        break;
      }
    }
  }

  for (const evt of events) {
    insertPendingUserMessages(evt.seq);
    turnProvider = evt.source_provider;

    switch (evt.kind) {
      case "text_delta": {
        if (thinkingBuffer.length > 0) {
          isThinkingStreaming = false;
          flushThinking();
        }
        const p = evt.payload as { text: string };
        currentTextBuffer += p.text;
        isStreaming = true;
        break;
      }
      case "text_complete":
        flushText();
        isStreaming = false;
        break;

      case "tool_start": {
        if (thinkingBuffer.length > 0) {
          isThinkingStreaming = false;
          flushThinking();
        }
        flushText();
        const p = evt.payload as { tool_id: string; tool_name: string; tool_input_summary: string };
        const result = toolResults.get(p.tool_id);
        const toolBlock: ContentBlock = {
          type: "tool_use",
          toolName: p.tool_name,
          toolId: p.tool_id,
          input: { summary: p.tool_input_summary },
          status: result ? (result.status === "error" ? "error" : "done") : "running",
        };
        if (result) {
          (toolBlock as Extract<ContentBlock, { type: "tool_use" }>).result = {
            output: result.output ?? "",
            isError: result.status === "error",
          };
        }
        currentBlocks.push(toolBlock);
        break;
      }
      case "tool_output":
        break;

      case "thinking": {
        const p = evt.payload as { text: string };
        thinkingBuffer += p.text;
        isThinkingStreaming = true;
        break;
      }
      case "thinking_complete":
        isThinkingStreaming = false;
        flushThinking();
        break;

      case "cost": {
        const p = evt.payload as { input_tokens: number; output_tokens: number; cost_usd: number };
        turnCostUsd = (turnCostUsd ?? 0) + p.cost_usd;
        turnTokens = {
          input: (turnTokens?.input ?? 0) + p.input_tokens,
          output: (turnTokens?.output ?? 0) + p.output_tokens,
        };
        break;
      }

      case "error": {
        flushText();
        const p = evt.payload as { message: string };
        currentBlocks.push({ type: "text", text: `⚠ ${p.message}` });
        break;
      }

      case "session_start":
      case "session_end":
      case "file_change":
      case "status":
      case "permission_request":
      case "permission_response":
        break;
    }
  }

  insertPendingUserMessages(Infinity);
  flushAssistantTurn();

  return turns;
}

export function useChatTurns(session: AiChatSession | null): ChatTurn[] {
  return useMemo(() => {
    if (!session) return [];
    return deriveContentBlocks(session.events, session.messages);
  }, [session]);
}
